import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchDramaCategories, fetchDramaList } from '@/api/member';
import { mapDramaCard, type CinemaSeries } from '@/utils/cinemaData';
import RankingPeriodTabs, { rankingViews, type RankingPeriod } from '../components/RankingPeriodTabs';

interface RankingCategory {
  id: string;
  label: string;
  categoryId: number;
  code: string;
}

const RANKING_LIMIT = 100;
const RANKING_PAGE_SIZE = 20;

function formatViews(value: number) {
  if (value >= 100_000_000) return `${(value / 100_000_000).toFixed(1).replace('.0', '')}亿`;
  if (value >= 10_000) return `${(value / 10_000).toFixed(1).replace('.0', '')}万`;
  return String(value);
}

function rankingUpdateTime(series: CinemaSeries) {
  for (const value of [series.latestEpisodeAt, series.updatedAt, series.createdAt]) {
    if (!value) continue;
    const timestamp = new Date(value.includes('T') ? value : value.replace(' ', 'T')).getTime();
    if (Number.isFinite(timestamp)) return timestamp;
  }
  return 0;
}

function RankBadge({ rank }: { rank: number }) {
  const tone = rank === 1
    ? 'bg-[#FBBF24] text-[#2A1600] ring-1 ring-[#FDE68A]/70 shadow-[0_0_14px_rgba(251,191,36,0.24)]'
    : rank === 2
      ? 'bg-[#D1D5DB] text-[#111827] ring-1 ring-white/45'
      : rank === 3
        ? 'bg-[#CD7F32] text-[#1F1308] ring-1 ring-[#F0B27A]/55'
        : 'text-foreground-400';

  return (
    <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold tabular-nums ${tone}`}>
      {rank}
    </span>
  );
}

function RankingCover({ series, eager }: { series: CinemaSeries; eager: boolean }) {
  const [failed, setFailed] = useState(false);

  return (
    <div className="relative w-[76px] h-[104px] md:w-[92px] md:h-[124px] flex-shrink-0 overflow-hidden rounded-md bg-background-800">
      {!failed && series.coverUrl ? (
        <img
          src={series.coverUrl}
          alt={series.title}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="w-full h-full flex items-center justify-center text-foreground-600" aria-hidden="true">
          <i className="ri-film-line text-2xl" />
        </span>
      )}
    </div>
  );
}

export default function CinemaRankingPage() {
  const { t, i18n } = useTranslation('cinema');
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [seriesRows, setSeriesRows] = useState<CinemaSeries[]>([]);
  const [categories, setCategories] = useState<RankingCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(RANKING_PAGE_SIZE);
  const [rankingPeriod, setRankingPeriod] = useState<RankingPeriod>('total');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetchDramaList(),
      fetchDramaCategories().catch(() => []),
    ])
      .then(([dramas, categoryRows]) => {
        if (cancelled) return;
        setSeriesRows(dramas.map(mapDramaCard));
        setCategories(categoryRows.map((item) => ({
          id: item.code || String(item.id),
          label: item.name,
          categoryId: Number(item.id) || 0,
          code: item.code || '',
        })));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.resolvedLanguage]);

  const categoryRows = useMemo(() => {
    if (categories.length) return categories;
    return Array.from(new Set(seriesRows.map((series) => series.category).filter(Boolean))).map((label) => ({
      id: label,
      label,
      categoryId: 0,
      code: '',
    }));
  }, [categories, seriesRows]);

  const categoryCount = (category: RankingCategory) => seriesRows.filter((series) => {
    if (category.categoryId > 0) return series.categoryId === category.categoryId;
    if (category.code) return series.categoryCode === category.code;
    return series.category === category.label;
  }).length;

  const rankedSeries = useMemo(() => {
    const category = categoryRows.find((item) => item.id === selectedCategory);
    const sourceRows = rankingPeriod === 'creator'
      ? seriesRows.filter((series) => series.sourceType === 'creator')
      : seriesRows;
    const filtered = !category
      ? sourceRows
      : sourceRows.filter((series) => {
          if (category.categoryId > 0) return series.categoryId === category.categoryId;
          if (category.code) return series.categoryCode === category.code;
          return series.category === category.label;
        });
    return [...filtered]
      .sort((a, b) => rankingPeriod === 'latest'
        ? rankingUpdateTime(b) - rankingUpdateTime(a) || Number(b.id) - Number(a.id)
        : rankingViews(b, rankingPeriod) - rankingViews(a, rankingPeriod) || b.views - a.views || Number(b.id) - Number(a.id))
      .slice(0, RANKING_LIMIT);
  }, [categoryRows, rankingPeriod, selectedCategory, seriesRows]);

  const totalViews = useMemo(
    () => rankedSeries.reduce((sum, series) => sum + rankingViews(series, rankingPeriod), 0),
    [rankedSeries, rankingPeriod],
  );
  const visibleSeries = useMemo(
    () => rankedSeries.slice(0, visibleCount),
    [rankedSeries, visibleCount],
  );
  const hasMore = visibleSeries.length < rankedSeries.length;

  useEffect(() => {
    setVisibleCount(RANKING_PAGE_SIZE);
  }, [rankingPeriod, selectedCategory]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (loading || !hasMore || !target) return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      setVisibleCount((current) => Math.min(current + RANKING_PAGE_SIZE, rankedSeries.length));
    }, { rootMargin: '240px 0px' });

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, rankedSeries.length]);

  const selectedLabel = selectedCategory === 'all'
    ? t('cinema_rankingAll')
    : categoryRows.find((item) => item.id === selectedCategory)?.label || '';
  const rankingPeriodLabel = {
    total: t('cinema_rankingTotal'),
    month: t('cinema_rankingMonth'),
    week: t('cinema_rankingWeek'),
    latest: t('cinema_rankingLatest'),
    creator: t('cinema_rankingCreator'),
  }[rankingPeriod];

  return (
    <main className="pt-14 md:pt-16 pb-20 md:pb-24">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <header className="flex flex-wrap items-center gap-3 py-5 md:py-7">
          <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg bg-accent-500/12 text-accent-500 flex-shrink-0">
            <i className="ri-trophy-line text-2xl md:text-3xl" />
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-50">{t('cinema_rankingTitle')}</h1>
            <p className="mt-1 text-xs md:text-sm text-foreground-500 truncate">
              {loading
                ? t('cinema_rankingLoading')
                : t('cinema_rankingStats', {
                    scope: selectedLabel,
                    period: rankingPeriodLabel,
                    count: rankedSeries.length,
                    views: formatViews(totalViews),
                  })}
            </p>
          </div>
          <div className="w-full overflow-x-auto sm:ml-auto sm:w-auto scrollbar-hide">
            <RankingPeriodTabs
              value={rankingPeriod}
              onChange={setRankingPeriod}
              periods={['total', 'month', 'week', 'latest', 'creator']}
              labels={{
                total: t('cinema_rankingTotal'),
                month: t('cinema_rankingMonth'),
                week: t('cinema_rankingWeek'),
                latest: t('cinema_rankingLatest'),
                creator: t('cinema_rankingCreator'),
              }}
            />
          </div>
        </header>

        <div className="-mx-4 w-[calc(100%+2rem)] overflow-x-auto overscroll-x-contain scrollbar-hide touch-pan-x [-webkit-overflow-scrolling:touch] md:mx-0 md:w-full">
          <div className="flex w-max min-w-full items-center gap-2 px-4 pb-4 md:px-0 md:pb-5">
            <button
              type="button"
              onClick={() => setSelectedCategory('all')}
              aria-pressed={selectedCategory === 'all'}
              className={`h-10 px-5 rounded-full border flex-shrink-0 whitespace-nowrap text-sm font-semibold transition-all active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F19744]/55 ${selectedCategory === 'all' ? 'border-[#F19744] bg-[#F19744] text-background-950 shadow-[0_5px_16px_rgba(241,151,68,0.2)]' : 'border-background-800 bg-background-900/75 text-foreground-400 hover:border-background-700 hover:text-foreground-100'}`}
            >
              {t('cinema_rankingAll')}
            </button>
            {categoryRows.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setSelectedCategory(category.id)}
                aria-pressed={selectedCategory === category.id}
                className={`h-10 px-4 rounded-full border flex-shrink-0 whitespace-nowrap text-sm font-medium transition-all active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F19744]/55 ${selectedCategory === category.id ? 'border-[#F19744] bg-[#F19744] text-background-950 shadow-[0_5px_16px_rgba(241,151,68,0.2)]' : 'border-background-800 bg-background-900/75 text-foreground-400 hover:border-background-700 hover:text-foreground-100'}`}
              >
                {category.label} <span className="opacity-70">({categoryCount(category)})</span>
              </button>
            ))}
          </div>
        </div>

        <section className="space-y-2.5 md:space-y-3" aria-label={t('cinema_rankingTitle')}>
          {loading
            ? Array.from({ length: 6 }, (_, index) => (
                <div key={index} className="h-[128px] md:h-[148px] rounded-lg bg-background-900/70 animate-pulse" />
              ))
            : visibleSeries.map((series, index) => {
                const label = rankingPeriod === 'creator'
                  ? t('cinema_creatorLabel')
                  : series.isHot ? t('cinema_hotLabel') : series.tagNew ? t('cinema_newLabel') : '';
                return (
                  <button
                    key={series.id}
                    type="button"
                    onClick={() => navigate(`/watch/${series.id}`)}
                    className="group w-full min-h-[128px] md:min-h-[148px] grid grid-cols-[36px_76px_minmax(0,1fr)] md:grid-cols-[44px_92px_minmax(0,1fr)] items-center gap-3 md:gap-4 p-3 md:p-4 rounded-lg bg-background-900/65 hover:bg-background-900 text-left transition-colors"
                  >
                    <RankBadge rank={index + 1} />
                    <RankingCover series={series} eager={index < 3} />
                    <span className="min-w-0 self-stretch py-1 flex flex-col justify-center">
                      <span className="flex items-center gap-2 min-w-0">
                        <strong className="text-base md:text-lg text-foreground-100 truncate">{series.title}</strong>
                        {label && (
                          <span className="flex-shrink-0 px-2 py-0.5 rounded text-[10px] md:text-xs font-bold bg-[#FB923C] text-background-950">
                            {label}
                          </span>
                        )}
                      </span>
                      <span className="mt-1 text-xs md:text-sm text-foreground-500 leading-5 line-clamp-2">
                        {series.description || series.category}
                      </span>
                      <span className="mt-auto pt-2 flex items-center gap-3 text-[11px] md:text-xs text-foreground-400">
                        <span className="flex items-center gap-1"><i className="ri-play-list-2-line text-accent-400" />{t('cinema_episodes', { count: series.totalEpisodes })}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line text-accent-400" />{formatViews(rankingViews(series, rankingPeriod))}</span>
                        <span className="hidden sm:flex items-center gap-1 min-w-0"><i className="ri-price-tag-3-line text-accent-400" /><span className="truncate">{series.category}</span></span>
                      </span>
                    </span>
                  </button>
                );
              })}
        </section>

        {!loading && hasMore && (
          <div ref={loadMoreRef} className="h-14 flex items-center justify-center" aria-hidden="true">
            <i className="ri-loader-4-line text-xl text-[#F19744] animate-spin" />
          </div>
        )}

        {!loading && rankedSeries.length === 0 && (
          <div className="py-20 text-center text-sm text-foreground-500">{t('cinema_rankingEmpty')}</div>
        )}
      </div>
    </main>
  );
}
